import { Monitor, Download, Play, ClipboardCheck, Wifi, Headphones, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

const features = [
  {
    icon: Wifi,
    title: 'Acceso 24/7',
    description: 'Ingresa cuando quieras, desde cualquier dispositivo conectado a internet',
    accent: 'from-blue-500 to-cyan-400',
  },
  {
    icon: Play,
    title: 'Videos y Clases',
    description: 'Contenido multimedia de alta calidad con instructores expertos',
    accent: 'from-violet-500 to-purple-400',
  },
  {
    icon: Download,
    title: 'Material Descargable',
    description: 'Guías, presentaciones y documentos para estudiar offline',
    accent: 'from-emerald-500 to-teal-400',
  },
  {
    icon: ClipboardCheck,
    title: 'Evaluaciones Online',
    description: 'Pruebas interactivas con resultados y retroalimentación inmediata',
    accent: 'from-orange-500 to-amber-400',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function VirtualClassroom() {
  return (
    <section id="aula" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 to-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-4">
            Plataforma Educativa
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-heading">
            Aula Virtual de{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Última Generación
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Nuestra plataforma educativa te permite acceder a todos tus cursos las 24 horas del día,
            los 7 días de la semana. Aprende a tu propio ritmo con contenido multimedia interactivo.
          </p>
        </motion.div>

        {/* Access CTA Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="relative max-w-2xl mx-auto">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 animate-pulse" />

            <div className="relative p-8 rounded-2xl bg-gradient-to-r from-blue-600/90 to-cyan-500/90 backdrop-blur-sm border border-white/20 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Accede al Aula Virtual Aquí
              </h3>
              <p className="text-blue-100 mb-6 max-w-md mx-auto">
                Ingresa a nuestra plataforma educativa y comienza tu formación profesional hoy mismo
              </p>

              <motion.a
                href={import.meta.env.VITE_MOODLE_URL || 'http://localhost:8080'}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(255,255,255,0.4)" }}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-xl shadow-2xl shadow-white/25 transition-all duration-300 hover:bg-blue-50"
              >
                <span className="relative">
                  {/* Pulsing ring animation */}
                  <span className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20" />
                  <Monitor className="w-6 h-6 relative" />
                </span>
                Ingresar al Aula Virtual
                <ExternalLink className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </motion.a>
            </div>
          </div>
        </motion.div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Feature cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-4"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.03, y: -5 }}
                  className="group p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${feature.accent} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Right: Platform mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            {/* Decorative elements */}
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-2xl" />

            {/* Main image container */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-500/10">
              <img
                src="/aula-virtual-mockup.jpg"
                alt="Plataforma de Aula Virtual Instituto Valparaíso"
                className="w-full h-auto object-cover"
              />

              {/* Overlay with floating elements */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent">
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full">
                      <Headphones className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white font-medium">Soporte en vivo</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full">
                      <Monitor className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm text-white font-medium">Multiplataforma</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating stats card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">+50</p>
                  <p className="text-xs text-slate-500">Horas de contenido</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default VirtualClassroom;
